import type { components } from "@soulbyte/core";

export const threadStages = ["draft", "active", "archived"] as const;
export type ThreadStage = (typeof threadStages)[number];

export const participantRoles = ["owner", "member", "viewer"] as const;
export type ParticipantRole = (typeof participantRoles)[number];

export type ContextGroup = components["schemas"]["ContextGroupDTO"];
export type Participant = components["schemas"]["ThreadParticipantDTO"];
export type Thread = components["schemas"]["UserThreadDTO"];

/** Newest activity first; tie-break by id for stable ordering across refetches. */
export function sortThreadsByRecency(threads: Thread[]): Thread[] {
  if (threads.length <= 1) return threads;

  return [...threads].sort((a, b) => {
    const diff = threadTimestamp(b) - threadTimestamp(a);
    if (diff !== 0) return diff;
    return (a.id ?? "").localeCompare(b.id ?? "");
  });
}

function threadTimestamp(thread: Thread): number {
  const updated = thread.updated_at ? Date.parse(thread.updated_at) : Number.NaN;
  if (!Number.isNaN(updated)) return updated;
  const created = thread.created_at ? Date.parse(thread.created_at) : Number.NaN;
  if (!Number.isNaN(created)) return created;
  return 0;
}

// local calendar day, so grouping matches what the user sees on their clock
function dayKey(timestamp: number): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export type ThreadDayGroup = { key: string; threads: Thread[] };

/**
 * groupThreadsByDay buckets recency-sorted threads into local calendar days,
 * preserving the incoming order both within and across buckets.
 */
export function groupThreadsByDay(threads: Thread[]): ThreadDayGroup[] {
  const groups: ThreadDayGroup[] = [];
  const byKey = new Map<string, Thread[]>();

  for (const thread of threads) {
    const key = dayKey(threadTimestamp(thread));
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      groups.push({ key, threads: bucket });
    }
    bucket.push(thread);
  }

  return groups;
}

/**
 * formatThreadDayLabel turns a day key into "Heute"/"Gestern" or an absolute
 * date like "30. Mai" (the year is added only for past years).
 */
export function formatThreadDayLabel(
  key: string,
  locale: string,
  labels: { today: string; yesterday: string },
): string {
  const today = dayKey(Date.now());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);

  if (key === today) return labels.today;
  if (key === dayKey(yesterdayDate.getTime())) return labels.yesterday;

  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const isCurrentYear = year === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: isCurrentYear ? undefined : "numeric",
  }).format(date);
}
